import React, { useEffect, useMemo, useState } from 'react';

import validate from '@vlabs/shared/validators';
import { Control } from '@vlabs/uikit';
import debounce from 'lodash/debounce';

import { useEventListFiltersTranslation } from '../../../i18n';
import { useEventListFilters } from '../../../useEventListFilters';
import st from '../../popovers.module.sass';

type CoordinateField = 'origin_longitude' | 'longitude_delta' | 'origin_latitude' | 'latitude_delta';

type CoordinateValues = Record<CoordinateField, string>;

type CoordinateErrors = Partial<Record<CoordinateField, string>>;

type Translate = ReturnType<typeof useEventListFiltersTranslation>['t'];

const RANGES: Record<CoordinateField, { min: number; max: number }> = {
  origin_longitude: { min: -180, max: 180 },
  longitude_delta: { min: 0, max: 90 },
  origin_latitude: { min: -90, max: 90 },
  latitude_delta: { min: 0, max: 90 },
};

const coordinateFields = Object.keys(RANGES) as CoordinateField[];

const isFilled = (value: string): boolean => value.trim() !== '';

function getCoordinateErrors(values: CoordinateValues, t: Translate): CoordinateErrors {
  const errors: CoordinateErrors = {};

  coordinateFields.forEach((field) => {
    const value = values[field];
    if (!isFilled(value)) return;

    const { min, max } = RANGES[field];
    const parsed = Number(value);
    if (Number.isNaN(parsed)) errors[field] = t('validation_number') ?? undefined;
    else if (parsed < min || parsed > max) errors[field] = t('validation_range', { left: min, right: max }) ?? undefined;
  });

  if (!isFilled(values.origin_longitude) && (isFilled(values.origin_latitude) || isFilled(values.longitude_delta))) {
    errors.origin_longitude = String(validate.required());
  }
  if (!isFilled(values.origin_latitude) && (isFilled(values.origin_longitude) || isFilled(values.latitude_delta))) {
    errors.origin_latitude = String(validate.required());
  }

  return errors;
}

function getFilterValues(values: CoordinateValues): Partial<Record<CoordinateField, number>> {
  return coordinateFields.reduce<Partial<Record<CoordinateField, number>>>((result, field) => ({
    ...result,
    [field]: isFilled(values[field]) ? Number(values[field]) : undefined,
  }), {});
}

const emptyValues: CoordinateValues = {
  origin_longitude: '',
  longitude_delta: '',
  origin_latitude: '',
  latitude_delta: '',
};

export function CoordinatesTab(): React.JSX.Element {
  const { t } = useEventListFiltersTranslation();
  const { filters, updateFilters } = useEventListFilters();

  const [local, setLocal] = useState<CoordinateValues>(emptyValues);
  const errors = getCoordinateErrors(local, t);

  const applyFilters = useMemo(() => debounce(updateFilters, 200), [updateFilters]);

  useEffect(() => {
    setLocal({
      origin_longitude: filters.origin_longitude?.toString() ?? '',
      longitude_delta: filters.longitude_delta?.toString() ?? '',
      origin_latitude: filters.origin_latitude?.toString() ?? '',
      latitude_delta: filters.latitude_delta?.toString() ?? '',
    });
  }, [filters]);

  const handleChange = (field: CoordinateField, value: string) => {
    const nextValues = { ...local, [field]: value };
    setLocal(nextValues);

    if (Object.keys(getCoordinateErrors(nextValues, t)).length > 0) {
      applyFilters.cancel();
      return;
    }
    applyFilters(getFilterValues(nextValues));
  };

  const renderInput = (field: CoordinateField) => (
    <div className={st.CoordinateInput}>
      <Control.Input
        errorMessage={errors[field]}
        hasError={!!errors[field]}
        id={field}
        isClearable
        label={t(field)}
        name={field}
        onChangeValue={(value) => handleChange(field, value)}
        onClear={() => handleChange(field, '')}
        value={local[field]}
      />
    </div>
  );

  return (
    <div className={st.CoordinatesTabWrapper}>
      <div className={st.CoordinatesInputs}>
        <div className={st.RowInputsWrapper}>
          {renderInput('origin_longitude')}
          {renderInput('longitude_delta')}
        </div>
        <div className={st.RowInputsWrapper}>
          {renderInput('origin_latitude')}
          {renderInput('latitude_delta')}
        </div>
      </div>
    </div>
  );
}
